// src/components/Navigation.js - Modified to add the Provider Dashboard link
import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import '../Navigation.css';

// Logo for the site
const siteLogo = '/Refound-Ed-Logo-Square-60x60.png';

// Default avatar for users without profile pictures
const defaultAvatar = 'https://ui-avatars.com/api/?background=55a820&color=fff&size=100&name=User';

function Navigation() {
  const { currentUser, isAdmin, isApprovedProvider, logout } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  
  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);
  
  // Close user dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Handle user logout
  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Failed to log out", error);
    }
  };
  
  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };
  
  // Toggle user dropdown menu
  const toggleUserMenu = () => {
    setUserMenuOpen(!userMenuOpen);
  };
  
  return (
    <nav className="main-nav">
      <div className="nav-container">
        {/* Logo and Site Title */}
        <div className="logo-container">
          <Link to="/" className="site-title-link">
            <img src={siteLogo} alt="RefoundEd" className="site-logo" />
            <div className="site-title">
              <span className="refound-text">Refound</span>
              <span className="ed-text">Ed</span>
            </div>
          </Link>
        </div>
        
        {/* Mobile Menu Toggle */}
        <button 
          className="mobile-menu-toggle" 
          onClick={toggleMobileMenu}
          aria-label="Toggle navigation menu"
        >
          {mobileMenuOpen ? '✕' : '☰'}
        </button>
        
        {/* Navigation Links */}
        <ul className={`nav-links ${mobileMenuOpen ? 'active' : ''}`}>
          <li className="nav-item">
            <NavLink 
              to="/" 
              className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}
              end
            >
              Home
            </NavLink>
          </li>
          <li className="nav-item">
            <NavLink 
              to="/listings" 
              className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}
            >
              Listings
            </NavLink>
          </li>
          <li className="nav-item">
            <NavLink 
              to="/contact" 
              className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}
            >
              Contact Us
            </NavLink>
          </li>
          
          {/* User Authentication Section */}
          {currentUser ? (
            <>
              {/* Show Add Listing only for approved providers */}
              {isApprovedProvider ? (
                <>
                  <li className="nav-item">
                    <Link to="/add-listing" className="add-listing-button">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                      </svg>
                      Add Listing
                    </Link>
                  </li>
                  {/* Add Provider Dashboard Link */}
                  <li className="nav-item">
                    <NavLink 
                      to="/provider-dashboard" 
                      className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}
                    >
                      Provider Dashboard
                    </NavLink>
                  </li>
                </>
              ) : (
                <li className="nav-item">
                  <Link to="/become-provider" className="add-listing-button">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19"></line>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                    Become a Provider
                  </Link>
                </li>
              )}
              
              {/* Show My Listings only for approved providers */}
              {isApprovedProvider && (
                <li className="nav-item">
                  <Link to="/my-listings" className="nav-link">My Listings</Link>
                </li>
              )}
              
              {/* My Calendar - accessible to all logged in users */}
              <li className="nav-item">
                <Link to="/my-calendar" className="nav-link">My Calendar</Link>
              </li>
              
              {/* Show Admin link for admins */}
              {isAdmin && (
                <li className="nav-item">
                  <Link to="/admin" className="nav-link admin-link">Admin Dashboard</Link>
                </li>
              )}
              
              <li className="nav-item">
                <button 
                  onClick={handleLogout} 
                  className="logout-btn"
                >
                  Sign Out
                </button>
              </li>
              
              <li className="user-dropdown" ref={userMenuRef}>
                <button 
                  className="user-dropdown-toggle" 
                  onClick={toggleUserMenu}
                  aria-label="Toggle user menu"
                >
                  <div className="user-avatar-container">
                    <img 
                      src={currentUser.photoURL || defaultAvatar} 
                      alt={currentUser.displayName || 'User'} 
                      className="user-avatar"
                    />
                  </div>
                </button>
                
                <div className={`user-menu ${userMenuOpen ? 'open' : ''}`}>
                  <div className="user-info-header">
                    <div className="user-display-name">
                      {currentUser.displayName || 'User'}
                    </div>
                    <div className="user-email">
                      {currentUser.email}
                    </div>
                  </div>
                  
                  {/* Add Provider Dashboard to dropdown menu */}
                  {isApprovedProvider && (
                    <Link to="/provider-dashboard" className="user-menu-item">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="7" height="7"></rect>
                        <rect x="14" y="3" width="7" height="7"></rect>
                        <rect x="14" y="14" width="7" height="7"></rect>
                        <rect x="3" y="14" width="7" height="7"></rect>
                      </svg>
                      Provider Dashboard
                    </Link>
                  )}
                  
                  {isApprovedProvider && (
                    <Link to="/my-listings" className="user-menu-item">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <line x1="16" y1="13" x2="8" y2="13"></line>
                        <line x1="16" y1="17" x2="8" y2="17"></line>
                        <polyline points="10 9 9 9 8 9"></polyline>
                      </svg>
                      My Listings
                    </Link>
                  )}
                  
                  <Link to="/my-calendar" className="user-menu-item">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                    My Calendar
                  </Link>
                  
                  {isAdmin && (
                    <Link to="/admin" className="user-menu-item">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                        <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
                      </svg>
                      Admin Dashboard
                    </Link>
                  )}
                  
                  <button onClick={handleLogout} className="user-menu-item logout-item">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                      <polyline points="16 17 21 12 16 7"></polyline>
                      <line x1="21" y1="12" x2="9" y2="12"></line>
                    </svg>
                    Sign Out
                  </button>
                </div>
              </li>
            </>
          ) : (
            <li className="nav-item">
              <NavLink 
                to="/signin" 
                className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}
              >
                Sign In
              </NavLink>
            </li>
          )}
        </ul>
      </div>
    </nav>
  );
}

export default Navigation;